"use client";

import * as React from "react";

const SCROLL_CONTAINER_ID = "app-main-scroll";
/** Desplazamiento mínimo por evento para contar dirección (evita micro-scrolls). */
const SCROLL_DELTA_MIN = 4;
/** Scroll acumulado hacia abajo antes de ocultar (histeresis anti-accidental). */
const SCROLL_DOWN_ACCUMULATE = 96;
/** Scroll acumulado hacia arriba para volver a mostrar (evita flicker). */
const SCROLL_UP_SHOW_ACCUMULATE = 36;
/** Distancia mínima al top para permitir ocultar. */
const SCROLL_HIDE_MIN_Y = 56;
/** Cerca del tope siempre visible. */
const SCROLL_TOP_SHOW = 12;
/** Si hay pausa larga entre eventos, reinicia acumuladores (evita disparos por micro-scrolls aislados). */
const SCROLL_GAP_RESET_MS = 420;

function getMainScrollContainer(): HTMLElement | null {
  return document.getElementById(SCROLL_CONTAINER_ID);
}

function readNavbarHeight(): number {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--app-navbar-height")
    .trim();
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 56;
}

function setNavbarOffset(visible: boolean, height: number) {
  const root = document.documentElement;
  root.style.setProperty(
    "--app-navbar-offset",
    visible ? `${height}px` : "0px"
  );
  root.dataset.navbarVisible = visible ? "true" : "false";
}

function isScrollableElement(el: HTMLElement): boolean {
  const { overflowY } = getComputedStyle(el);
  return (
    (overflowY === "auto" ||
      overflowY === "scroll" ||
      overflowY === "overlay") &&
    el.scrollHeight > el.clientHeight + 2
  );
}

function isTrackedScrollElement(el: HTMLElement): boolean {
  const mainScroll = getMainScrollContainer();
  if (!mainScroll?.contains(el)) return false;

  if (el.closest("[data-navbar-scroll-ignore]")) return false;
  if (el.id === SCROLL_CONTAINER_ID) return true;

  return isScrollableElement(el);
}

export function useAppNavbar(disabled = false, ignoreResize = false) {
  const headerRef = React.useRef<HTMLElement>(null);
  const [visible, setVisible] = React.useState(true);
  const visibleRef = React.useRef(true);
  const ignoreResizeRef = React.useRef(ignoreResize);
  const downAccumRef = React.useRef(0);
  const upAccumRef = React.useRef(0);
  const heightRef = React.useRef(56);
  const lastWindowScrollRef = React.useRef(0);
  const lastScrollTsRef = React.useRef(0);
  const lastScrollByElementRef = React.useRef(
    new WeakMap<HTMLElement, number>()
  );

  // Mantener ref actualizada para ignoreResize (useLayoutEffect para que se ejecute antes del paint)
  React.useLayoutEffect(() => {
    ignoreResizeRef.current = ignoreResize;
  }, [ignoreResize]);

  const applyVisible = React.useCallback((next: boolean) => {
    // No cambiar visibilidad durante transición
    if (ignoreResizeRef.current) return;
    if (visibleRef.current === next) return;
    visibleRef.current = next;
    setVisible(next);
    setNavbarOffset(next, heightRef.current);
  }, []);

  React.useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const syncHeight = () => {
      if (ignoreResizeRef.current) return; // No actualizar durante transición
      const h = Math.round(el.getBoundingClientRect().height);
      if (h < 1) return;
      heightRef.current = h;
      document.documentElement.style.setProperty(
        "--app-navbar-height",
        `${h}px`
      );
      setNavbarOffset(visibleRef.current, h);
    };

    syncHeight();
    const ro = new ResizeObserver(syncHeight);
    ro.observe(el);
    window.addEventListener("resize", syncHeight);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", syncHeight);
    };
  }, []);

  React.useEffect(() => {
    if (disabled || ignoreResizeRef.current) {
      applyVisible(true);
      return;
    }

    let attached = false;
    let rafId = 0;
    let retries = 0;
    let mainScroll: HTMLElement | null = null;

    const updateVisibilityFromScroll = (scrollY: number, lastY: number) => {
      // No procesar scroll durante transición
      if (ignoreResizeRef.current) return;

      const now = performance.now();
      if (now - lastScrollTsRef.current > SCROLL_GAP_RESET_MS) {
        downAccumRef.current = 0;
        upAccumRef.current = 0;
      }
      lastScrollTsRef.current = now;

      const delta = scrollY - lastY;

      if (scrollY <= SCROLL_TOP_SHOW) {
        downAccumRef.current = 0;
        upAccumRef.current = 0;
        applyVisible(true);
        return;
      }

      if (Math.abs(delta) < SCROLL_DELTA_MIN) return;

      if (delta > 0) {
        upAccumRef.current = 0;
        downAccumRef.current += delta;

        if (
          scrollY >= SCROLL_HIDE_MIN_Y &&
          (downAccumRef.current >= SCROLL_DOWN_ACCUMULATE || delta >= 44)
        ) {
          applyVisible(false);
        }
      } else {
        downAccumRef.current = 0;
        upAccumRef.current += Math.abs(delta);

        if (upAccumRef.current >= SCROLL_UP_SHOW_ACCUMULATE || delta <= -32) {
          applyVisible(true);
        }
      }
    };

    const handleElementScroll = (target: HTMLElement) => {
      const scrollY = target.scrollTop;
      const lastY = lastScrollByElementRef.current.get(target) ?? scrollY;
      lastScrollByElementRef.current.set(target, scrollY);
      updateVisibilityFromScroll(scrollY, lastY);
    };

    const handleWindowScroll = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      const lastY = lastWindowScrollRef.current || scrollY;
      lastWindowScrollRef.current = scrollY;
      updateVisibilityFromScroll(scrollY, lastY);
    };

    const onScrollCapture = (event: Event) => {
      const target = event.target;

      if (target instanceof HTMLElement && isTrackedScrollElement(target)) {
        handleElementScroll(target);
        return;
      }

      // Fallback para páginas que por estilos terminaron scrolleando el documento.
      if (target === document || target === document.documentElement) {
        handleWindowScroll();
      }
    };

    const attach = () => {
      if (attached) return;

      mainScroll = getMainScrollContainer();
      if (!mainScroll) {
        if (retries < 30) {
          retries += 1;
          rafId = requestAnimationFrame(attach);
        }
        return;
      }

      attached = true;
      lastScrollByElementRef.current.set(mainScroll, mainScroll.scrollTop);
      lastWindowScrollRef.current =
        window.scrollY || document.documentElement.scrollTop || 0;
      downAccumRef.current = 0;
      upAccumRef.current = 0;
      lastScrollTsRef.current = 0;

      // Listener directo: evita depender del capture global cuando el contenedor
      // principal cambia de posición/tamaño por el auto-hide del navbar.
      mainScroll.addEventListener("scroll", onScrollCapture, { passive: true });
      document.addEventListener("scroll", onScrollCapture, {
        capture: true,
        passive: true
      });
      window.addEventListener("scroll", handleWindowScroll, { passive: true });
    };

    attach();

    return () => {
      cancelAnimationFrame(rafId);
      mainScroll?.removeEventListener("scroll", onScrollCapture);
      document.removeEventListener("scroll", onScrollCapture, true);
      window.removeEventListener("scroll", handleWindowScroll);
      lastScrollByElementRef.current = new WeakMap();
    };
  }, [disabled, applyVisible]);

  React.useEffect(() => {
    if (disabled) applyVisible(true);
  }, [disabled, applyVisible]);

  React.useEffect(() => {
    setNavbarOffset(true, heightRef.current);
    return () => {
      const root = document.documentElement;
      root.style.removeProperty("--app-navbar-offset");
      root.style.removeProperty("--app-navbar-height");
      delete root.dataset.navbarVisible;
    };
  }, []);

  return {
    headerRef,
    visible,
    navbarHeight: heightRef.current
  };
}

/** Altura actual del navbar (para posicionar overlays). */
export function getAppNavbarHeight(): number {
  return readNavbarHeight();
}
